import { DEVELOPER_API_PATH, consoleRoutes, webappDisplayUrl, webappPublicUrl } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, StatusBadge } from "@sagot/ui";
import type { ChannelOut } from "../shared/types";
import { ChannelCard, ChannelStatusBadge, ConversationsMetric } from "./channel-card";
import { useChannelSwitch, type ChannelPageContext } from "./channel-context";
import { CHANNEL_ICON, apiKeyCount, widgetDomainCount } from "./channels-model";
import { useWebhookCount } from "./use-channels";

const P = "deployKnowledge.channels";

interface CardProps {
  channel: ChannelOut;
  ctx: ChannelPageContext;
}

export function WidgetCard({ channel, ctx }: CardProps) {
  const { t } = useTranslation();
  const toggle = useChannelSwitch(ctx);
  const domains = widgetDomainCount(channel);
  return (
    <ChannelCard
      testId="channel-widget"
      icon={CHANNEL_ICON.widget}
      title={t(`${P}.names.widget`)}
      toggle={toggle(channel)}
      status={<ChannelStatusBadge channel={channel} />}
      meta={domains > 0 ? t(`${P}.widget.domains`, { count: domains }) : t(`${P}.widget.noDomains`)}
      description={t(`${P}.widget.description`)}
      footer={
        <>
          <ConversationsMetric count={channel.conversations_7d} />
          <Button asChild variant="secondary" size="sm">
            <AppLink href={consoleRoutes.widgetStudio(ctx.wsSlug, ctx.agentId)}>
              <Icon name="palette" />
              {t(`${P}.widget.customize`)}
            </AppLink>
          </Button>
        </>
      }
    />
  );
}

export function WebappCard({ channel, ctx }: CardProps) {
  const { t } = useTranslation();
  const toggle = useChannelSwitch(ctx);
  const url = webappPublicUrl(ctx.wsSlug);
  return (
    <ChannelCard
      testId="channel-webapp"
      icon={CHANNEL_ICON.webapp}
      title={t(`${P}.names.webapp`)}
      toggle={toggle(channel)}
      status={<ChannelStatusBadge channel={channel} />}
      meta={<span className="font-mono">{webappDisplayUrl(ctx.wsSlug)}</span>}
      footer={
        <>
          <ConversationsMetric count={channel.conversations_7d} />
          <div className="flex gap-1.5">
            <Button asChild variant="ghost" size="sm">
              <AppLink href={consoleRoutes.settings(ctx.wsSlug, "domains")}>{t(`${P}.webapp.domain`)}</AppLink>
            </Button>
            <Button asChild variant="secondary" size="sm">
              <a href={url} target="_blank" rel="noopener noreferrer" aria-label={t(`${P}.webapp.openLabel`)}>
                <Icon name="external-link" />
                {t(`${P}.webapp.open`)}
              </a>
            </Button>
          </div>
        </>
      }
    >
      <p className="mt-2 flex items-center gap-1.5 text-xs text-fg-muted">
        <Icon name="shield-check" className="size-3.5 text-success" />
        {t(`${P}.webapp.domainNote`)}
      </p>
    </ChannelCard>
  );
}

/** No Sagot app to publish: the assistant goes inside the customer's own app (install guide). */
export function MobileCard({ ctx }: Pick<CardProps, "ctx">) {
  const { t } = useTranslation();
  return (
    <ChannelCard
      testId="channel-mobile"
      icon={CHANNEL_ICON.mobile}
      title={t(`${P}.names.mobile`)}
      description={t(`${P}.mobile.description`)}
      footer={
        <>
          <span className="text-xs text-fg-subtle">{t(`${P}.mobile.note`)}</span>
          <Button asChild variant="secondary" size="sm">
            <AppLink href={consoleRoutes.widgetStudio(ctx.wsSlug, ctx.agentId, "install")}>
              <Icon name="code" />
              {t(`${P}.mobile.guide`)}
            </AppLink>
          </Button>
        </>
      }
    />
  );
}

export function ApiCard({ channel, ctx }: CardProps) {
  const { t } = useTranslation();
  const toggle = useChannelSwitch(ctx);
  const keys = apiKeyCount(channel);
  return (
    <ChannelCard
      testId="channel-api"
      icon={CHANNEL_ICON.api}
      title={t(`${P}.names.api`)}
      toggle={toggle(channel)}
      status={keys > 0 ? <StatusBadge status="success" label={t(`${P}.api.keys`, { count: keys })} /> : <StatusBadge status="neutral" label={t(`${P}.api.noKeys`)} />}
      description={t(`${P}.api.description`)}
      footer={
        <>
          <ConversationsMetric count={channel.conversations_7d} />
          <Button asChild variant="secondary" size="sm">
            <AppLink href={consoleRoutes.settings(ctx.wsSlug, "api")}>
              <Icon name="key" />
              {t(`${P}.api.keysLink`)}
            </AppLink>
          </Button>
        </>
      }
    >
      <div className="mt-2 rounded-md bg-sunken px-2.5 py-2 font-mono text-[11px]">
        <span className="font-bold text-primary-ink">POST</span> {DEVELOPER_API_PATH}/agents/{"{id}"}/chat
      </div>
    </ChannelCard>
  );
}

export function WebhooksCard({ ctx }: { ctx: ChannelPageContext }) {
  const { t } = useTranslation();
  const webhooks = useWebhookCount();
  const count = webhooks.data?.filter((w) => w.enabled).length;
  return (
    <ChannelCard
      testId="channel-webhooks"
      icon="webhook"
      title={t(`${P}.webhooks.title`)}
      status={
        count === undefined ? undefined : count > 0 ? <StatusBadge status="success" label={t(`${P}.webhooks.endpoints`, { count })} /> : <StatusBadge status="neutral" label={t(`${P}.webhooks.none`)} />
      }
      description={t(`${P}.webhooks.description`)}
      footer={
        <>
          <span />
          <Button asChild variant="secondary" size="sm">
            <AppLink href={consoleRoutes.settings(ctx.wsSlug, "webhooks")}>{t(`${P}.webhooks.manage`)}</AppLink>
          </Button>
        </>
      }
    />
  );
}

function SoonRow({ icon, title, body }: { icon: "phone" | "smartphone"; title: string; body: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3">
      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-sunken text-fg-subtle">
        <Icon name={icon} className="size-5" />
      </span>
      <div className="flex-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-bold">{title}</h3>
          <Badge tone="warning">
            <Icon name="clock" className="size-3" />
            {t(`${P}.comingSoon.badge`)}
          </Badge>
        </div>
        <p className="mt-1 text-xs text-fg-muted">{body}</p>
      </div>
    </div>
  );
}

/** Viber teaser (no waitlist endpoint exists, so no "Notify me" action). No SMS channel (DEC-041). */
export function ComingSoonCard() {
  const { t } = useTranslation();
  return (
    <article className="card flex flex-col bg-surface-2" data-testid="channel-coming-soon">
      <div className="card-body flex-1 space-y-4">
        <SoonRow icon="phone" title={t(`${P}.comingSoon.viber`)} body={t(`${P}.comingSoon.viberBody`)} />
      </div>
      <div className="border-t border-line px-5 py-3 text-xs text-fg-subtle">{t(`${P}.comingSoon.note`)}</div>
    </article>
  );
}
